/**
 * Client for the mt-ea MetaTrader bridge service (backend-apps/mt-ea).
 * All endpoints require the beta-widgets role — callers are already gated by
 * useHasBetaWidgetsRole, so a 403 here means role drift, not a bug in the widget.
 */
import keycloakInstance from "@/initKeycloak";
import axios from "axios";
import { attachRetryOn401 } from "@/shared/api/interceptors/retryOn401";

export const baseURL =
  import.meta.env.VITE_MT_EA_BASE_URL || "http://localhost:8000";

export const client = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
});

client.interceptors.request.use(async (config) => {
  if (keycloakInstance && keycloakInstance.authenticated) {
    try {
      await keycloakInstance.updateToken(30);
    } catch (error) {
      // Transient refresh failure — send the existing token; retryOn401 covers
      // the genuinely-invalid case.
      console.error("[mt-ea] token refresh error in interceptor:", error);
    }
    config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
  }
  return config;
});

attachRetryOn401(client);

// --- types (mirror app/api/widget.py responses) ---

export type BrokerAccountState = {
  balance: number;
  equity: number;
  margin_free: number;
  currency: string;
  leverage: number;
};

export type BrokerAccount = {
  account_id: number;
  label: string;
  status: "pending" | "connected" | "disabled";
  connected: boolean;
  kill_switch: boolean;
  /** Terminal is on a non-demo broker server. */
  live_server: boolean;
  /** User has explicitly enabled live (real-money) trading on this connection. */
  allow_live: boolean;
  /** live_server && !allow_live — orders are refused until opted in. */
  live_blocked: boolean;
  max_lots: number;
  mt_version: 4 | 5;
  mt_account_number: string;
  broker_server: string;
  last_seen_at: string | null;
  state: BrokerAccountState | null;
};

export type BrokerSymbol = {
  canonical: string;
  /** null → not offered by this user's broker */
  broker_symbol: string | null;
};

export type TagMapEntry = {
  tag_id: number | null;
  tag: string;
  canonical: string;
  /** Weak tags (bare currencies) drive auto-switch but not quick-trade chips. */
  weak: boolean;
};

export type BrokerQuote = {
  canonical: string;
  symbol: string;
  bid: number;
  ask: number;
  ts: number;
};

export type OrderType =
  | "market_buy"
  | "market_sell"
  | "limit_buy"
  | "limit_sell"
  | "stop_buy"
  | "stop_sell";

export type TradeCommand = {
  id: number;
  type: OrderType | "close_position" | "modify_sltp" | "cancel_order";
  symbol: string;
  broker_symbol: string;
  lots: number | null;
  price: number | null;
  sl: number | null;
  tp: number | null;
  position_ticket: number | null;
  status: "pending" | "delivered" | "filled" | "rejected" | "expired";
  result_price: number | null;
  result_ticket: number | null;
  error_text: string;
  created_at: string | null;
  resolved_at: string | null;
};

export type BrokerPosition = {
  ticket: number;
  symbol: string;
  side: "buy" | "sell";
  lots: number;
  open_price: number;
  sl: number | null;
  tp: number | null;
  profit: number;
  opened_at: string | null;
};

export type WorkingOrder = {
  ticket: number;
  symbol: string;
  type: "buy_limit" | "sell_limit" | "buy_stop" | "sell_stop";
  lots: number;
  price: number;
  sl: number | null;
  tp: number | null;
  placed_at: string | null;
};

export type BrokerStreamEvent =
  | { type: "hello"; account_id: number }
  | { type: "status"; status: string }
  | { type: "quote"; symbol: string; bid: number; ask: number; ts: number }
  | { type: "positions"; positions: BrokerPosition[] }
  | { type: "working_orders"; orders: WorkingOrder[] }
  | { type: "account_state"; state: BrokerAccountState }
  | { type: "command"; command: TradeCommand };

// --- calls ---

export const createPairing = async (label: string, mtVersion: 4 | 5) =>
  (await client.post("/api/pairing", { label, mt_version: mtVersion })).data as {
    account_id: number;
    pairing_token: string;
    mt_version: 4 | 5;
  };

export const revokePairing = async () => {
  await client.delete("/api/pairing");
};

/** Resolves to null when no broker is paired (404). */
export const getAccount = async (): Promise<BrokerAccount | null> => {
  try {
    return (await client.get("/api/account")).data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
};

/** Explicit opt-in/out for trading on a live (non-demo) server. */
export const setAllowLive = async (enabled: boolean) =>
  (await client.post("/api/account/allow-live", { enabled })).data as {
    allow_live: boolean;
    live_blocked: boolean;
  };

export const getSymbols = async () =>
  (await client.get("/api/symbols")).data as {
    symbols: BrokerSymbol[];
    tag_map: TagMapEntry[];
  };

/** 404 = subscribed but no tick yet — treated as "no quote yet", not an error. */
export const getQuote = async (canonical: string): Promise<BrokerQuote | null> => {
  try {
    return (await client.get(`/api/quote/${canonical}`)).data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
};

export const createOrder = async (order: {
  idempotency_key: string;
  type: OrderType;
  symbol: string;
  lots: number;
  price?: number | null; // required for limit/stop
  sl?: number | null;
  tp?: number | null;
}) => (await client.post("/api/orders", order)).data as TradeCommand;

export const getWorkingOrders = async () =>
  (await client.get("/api/working-orders")).data.orders as WorkingOrder[];

export const cancelWorkingOrder = async (ticket: number, idempotencyKey: string) =>
  (
    await client.post(`/api/working-orders/${ticket}/cancel`, {
      idempotency_key: idempotencyKey,
    })
  ).data as TradeCommand;

export const getOrder = async (id: number) =>
  (await client.get(`/api/orders/${id}`)).data as TradeCommand;

export const getPositions = async () =>
  (await client.get("/api/positions")).data.positions as BrokerPosition[];

export const closePosition = async (ticket: number, idempotencyKey: string) =>
  (
    await client.post(`/api/positions/${ticket}/close`, {
      idempotency_key: idempotencyKey,
    })
  ).data as TradeCommand;

/** EventSource can't set headers — the service accepts ?access_token=. */
export const streamUrl = () =>
  `${baseURL}/api/stream?access_token=${keycloakInstance?.token ?? ""}`;
